import type { ApiResponse, MeResponseData, TenantPermissionsResponseData } from '@/shared/types';
import api from '@/shared/api/client';
import type { LoginResponseData } from './types';

async function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

export const authApi = {
  login: (data: { email: string; password: string; tenantId?: string }) =>
    api.post<ApiResponse<LoginResponseData>>('/auth/login', data),

  verifyMfa: (data: { mfaToken: string; code: string }) =>
    api.post<ApiResponse<LoginResponseData>>('/auth/mfa/verify', data),

  register: (data: {
    email: string;
    password: string;
    name: string;
    role?: string;
  }) => api.post<ApiResponse<any>>('/auth/register', data),

  refreshToken: () =>
    api.post<ApiResponse<{ user: any; accessToken: string }>>(
      '/auth/refresh',
      {},
    ),

  logout: () => api.post<ApiResponse<any>>('/auth/logout'),

  getCurrentUser: () => api.get<ApiResponse<MeResponseData>>('/auth/me'),

  updateProfile: (data: { name: string }) =>
    api.put<ApiResponse<any>>('/auth/profile', data),

  uploadProfileAvatar: async (file: File) =>
    api.put<ApiResponse<any>>('/auth/profile/avatar', {
      avatarUrl: await fileToDataUrl(file),
    }),

  changePassword: (data: { currentPassword: string; newPassword: string }) =>
    api.post<ApiResponse<any>>('/auth/change-password', data),

  forgotPassword: (data: { email: string; tenantId?: string }) =>
    api.post<ApiResponse<any>>('/auth/forgot-password', data),

  resetPassword: (data: { token: string; password: string }) =>
    api.post<ApiResponse<any>>('/auth/reset-password', data),

  acceptInvitation: (data: {
    token: string;
    password: string;
    name?: string;
  }) => api.post<ApiResponse<any>>('/auth/invitations/accept', data),

  regenerateRecoveryCodes: () =>
    api.post<ApiResponse<{ codes: string[] }>>(
      '/auth/recovery-codes/regenerate',
    ),

  exitImpersonation: () =>
    api.post<ApiResponse<any>>('/auth/impersonation/exit', {}),
};

export const apiKeyApi = {
  list: () => api.get<ApiResponse<Record<string, unknown>[]>>('/api-keys'),
  create: (data: {
    name: string;
    permissions?: string[];
    rateLimit?: number;
    expiresAt?: string | null;
  }) => api.post<ApiResponse<{ apiKey: string }>>('/api-keys', data),
  revoke: (id: string) => api.delete<ApiResponse<{ success: boolean }>>(`/api-keys/${id}`),
};

export const mfaApi = {
  status: () =>
    api.get<ApiResponse<{ enabled: boolean; configured: boolean }>>(
      '/mfa/status',
    ),

  setup: () =>
    api.post<ApiResponse<{ secret: string; otpauthUrl: string }>>('/mfa/setup'),

  enable: (data: { code: string; password: string }) =>
    api.post<ApiResponse<{ enabled: boolean; configured: boolean }>>(
      '/mfa/enable',
      data,
    ),

  disable: (data: { code: string; password: string }) =>
    api.post<ApiResponse<{ enabled: boolean; configured: boolean }>>(
      '/mfa/disable',
      data,
    ),
};

const webAuthnOrigin = () =>
  typeof window !== 'undefined' ? window.location.origin : undefined;

export const passkeyApi = {
  check: (email: string) =>
    api.get<ApiResponse<{ hasPasskeys: boolean }>>(
      `/passkeys/check?email=${encodeURIComponent(email)}`,
    ),

  registerStart: (data: { email: string; password: string }) =>
    api.post<ApiResponse<{ options: any; challenge: string }>>(
      '/passkeys/register/start',
      {
        ...data,
        origin: webAuthnOrigin(),
      },
    ),

  registerVerify: (data: {
    email: string;
    password: string;
    registrationResponse: any;
    expectedChallenge: string;
    credentialName?: string;
  }) =>
    api.post<ApiResponse<any>>('/passkeys/register/verify', {
      ...data,
      origin: webAuthnOrigin(),
    }),

  authenticateStart: (data: { email: string }) =>
    api.post<ApiResponse<{ options: any; userId: string; challenge: string }>>(
      '/passkeys/authenticate/start',
      { ...data, origin: webAuthnOrigin() },
    ),

  authenticateVerify: (data: {
    authenticationResponse: any;
    expectedChallenge: string;
    userId: string;
  }) =>
    api.post<ApiResponse<LoginResponseData>>('/passkeys/authenticate/verify', {
      ...data,
      origin: webAuthnOrigin(),
    }),

  list: () => api.get<ApiResponse<any[]>>('/passkeys/list'),

  delete: (id: string) =>
    api.post<ApiResponse<any>>('/passkeys/delete', { id }),

  updateName: (id: string, name: string) =>
    api.post<ApiResponse<any>>('/passkeys/update-name', { id, name }),
};
